import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from '@mui/material';
import Heading from '../../Components/Heading/heading.tsx';
import { getZohoCustomer, addClient, getCompanies } from './action.ts';
import { toast } from 'react-toastify';

interface AddClientModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddClientModal: React.FC<AddClientModalProps> = ({ open, onClose, onSuccess }) => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [error, setError] = useState<string>('');
  const [customerData, setCustomerData] = useState<any>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (!/^\d*$/.test(value)) {
      return;
    }

    setMobileNumber(value);
    
    if (value.length < 10) {
      setCustomerData(null);
    }
    
    if (value.length > 0 && /^[0-5]/.test(value)) {
      setError('Phone number cannot start with 0, 1, 2, 3, 4, or 5.');
      return;
    }
    
    if (value.length > 10) {
      setError('Phone number cannot exceed 10 digits.');
      return;
    }
    
    setError('');
    
    if (value.length === 10) {
      getZohoCustomer(
        value,
        (response: any) => {
          if (response?.status === 200 && response?.data?.length > 0) {
            setCustomerData(response.data[0]);
            setError('');
          } else {
            setError('No customer found for this number.');
            setCustomerData(null);
          }
        },
        (errMessage: any) => {
          setError(typeof errMessage === 'string' ? errMessage : 'Customer not found or server error.');
          setCustomerData(null);
        }
      );

      getCompanies(
        10,
        0,
        value,
        (response: any) => {
          console.log('Companies fetched successfully:', response);
        },
        (errMessage: any) => {
          console.error('Error fetching companies:', errMessage);
        }
      );
    }
  };

  const handleConfirm = () => {
    if (mobileNumber.length !== 10 || error) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }

    if (!customerData) {
      setError('Please ensure customer details are loaded before confirming.');
      return;
    }
    
    addClient(
      customerData.zoho_customer_id || "",
      customerData.name || "",                                 
      customerData.email || "",                                
      mobileNumber,                                              
      customerData.billing_address?.address || "",              
      customerData.billing_address?.zip || "",                  
      customerData.billing_address?.state || "",            
      customerData.pan_number || "",                          
      customerData.gst_number || "",                         
      customerData.tan_number || "",                            
      customerData.cin_number || "",                            
      customerData.contact_persons?.[0]?.first_name || "",     
      customerData.contact_persons?.[0]?.email || "",        
      customerData.contact_persons?.[0]?.phone || "",          
      customerData.contact_persons?.[0]?.designation || "",     
      (response: any) => {
        toast.success(response?.message || 'Client added successfully!');
        if (onSuccess) onSuccess(); 
        onClose();
      },
      (errMessage: any) => {
        toast.error(errMessage?.message);
      }
    );
  };

  const formatAddress = (addressObj: any) => {
    if (!addressObj) return '';
    const { address, city, state, zip, country } = addressObj;
    const parts = [address, city, state, zip, country].filter(part => part && part.trim() !== '');
    return parts.join(', ');
  };

  const DisabledField = ({ label, value }: { label: string; value: string }) => {
    if (!value || value.trim() === '') {
      return null;
    }

    return (
      <Box display="flex" alignItems="center" justifyContent="center" gap={2} mt={1.5}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '110px' }}>
          {label}
        </Typography>
        <input
          type="text"
          value={value}
          disabled
          style={{ 
            width: '180px', 
            padding: '8px', 
            border: '1px solid #ccc',
            backgroundColor: '#f5f5f5',
            color: '#666',
            outline: 'none',
            boxSizing: 'border-box' 
          }}
        />
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: '6px', overflow: 'hidden' }
      }}
    >
      <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}> 
        <Heading title="Add Client" onClose={onClose} closeIcon="true"/>
      </Box>

      <DialogContent sx={{ padding: '40px 30px' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} mt={2}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '110px' }}>
              Mobile Number
            </Typography>
            <input
              type="text"
              value={mobileNumber}
              onChange={handlePhoneChange}
              style={{ 
                width: '180px', 
                padding: '8px', 
                border: `1px solid ${error ? 'red' : '#000'}`, 
                outline: 'none',
                boxSizing: 'border-box' 
              }}
            />
          </Box>
          {error && (
            <Typography color="error" sx={{ fontSize: '12px', marginLeft: '130px', alignSelf: 'flex-start' }}>
              {error}
            </Typography>
          )}
          {customerData && (
            <Box mt={3} width="100%" display="flex" flexDirection="column" alignItems="center">
              <DisabledField label="Name" value={customerData.name} />
              <DisabledField label="Email" value={customerData.email} />
              <DisabledField label="GST Number" value={customerData.gst_number} />
              <DisabledField label="PAN Number" value={customerData.pan_number} />
              <DisabledField label="Billing Address" value={formatAddress(customerData.billing_address)} />
              <DisabledField label="Shipping Address" value={formatAddress(customerData.shipping_address)} />
              <Box mt={3} px={2} py={1} sx={{ backgroundColor: '#fff4e5', borderRadius: '4px', border: '1px solid #ffe0b2', width: '80%' }}>
                <Typography color="warning.dark" sx={{ fontSize: '12px', textAlign: 'center', fontWeight: 500 }}>
                  If there is any mismatch in the details above, please contact the finance manager or check{' '}
                  <Link 
                    href={`https://books.zoho.in/app/60042545279#/contacts/${customerData.zoho_customer_id}?filter_by=Status.All&per_page=25&sort_column=customer_name&sort_order=A`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    color="inherit"
                    sx={{ textDecoration: 'underline', fontWeight: 'bold' }}
                  >
                    Zoho
                  </Link>.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions style={{ justifyContent: 'center' }}>
        <Button onClick={onClose} className="custom-cancel-button" style={{ marginRight: '10px' }}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} className="custom-button" style={{ marginLeft: '10px' }}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddClientModal;